import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shapes, Lock, Check, Wand2, Loader2 } from "lucide-react";
import { STYLE_PACKS, isStylePackV2, setRecipeOverride, DEFAULT_PACKS } from "../lib/stylePacks";
import { tap } from "../lib/haptic";
import { track } from "../lib/track";

/**
 * PackIterationStrip — inline pack swapper that lives BELOW the preview
 * card on CardDetail. Tapping a pack tile instantly re-applies that
 * pack's first (free) variant to the current moodcard via
 * `setRecipeOverride` + the parent's `onChange` trigger, which bumps the
 * render-tick so MoodCard re-resolves its recipe.
 *
 * Why a strip instead of the existing RestyleDrawer:
 *   • Iteration is a FIRST-CLASS post-generation behavior, not buried in
 *     a drawer. The user explicitly said "they need to see it in real life
 *     how it will look with every option" — that requires the swatches to
 *     be permanently visible next to the card.
 *   • Free users can scroll the whole row, see locked packs marked with a
 *     padlock — premium upsell happens in context.
 *
 * Props:
 *   moodId           — the moodcard id (URL :id)
 *   currentPack      — the moodcard's current pack id ("cinematic", etc.)
 *   currentLane      — emotion lane (drives which packs are free)
 *   isPremium        — gate flag
 *   onChange         — called after a pack swap (parent re-renders preview)
 *   onPremiumLocked  — called when a free user taps a locked pack
 */
export default function PackIterationStrip({
    moodId,
    currentPack,
    currentTone = null,
    regenCount = 0,
    regenLimit = 3,
    isPremium = false,
    onChange,
    onPremiumLocked,
    onRegenerateTone,
}) {
    const [regenTone, setRegenTone] = useState(null); // which tone is in flight

    const handleToneTap = async (tone) => {
        if (regenTone) return; // one at a time
        if (tone === currentTone) return; // no-op
        setRegenTone(tone);
        tap();
        try {
            if (onRegenerateTone) await onRegenerateTone(tone);
        } finally {
            setRegenTone(null);
        }
    };
    const handlePick = (packId) => {
        if (!isStylePackV2(packId)) return;
        const packDef = STYLE_PACKS[packId];
        if (!packDef) return;
        const free = packDef.variants[0]?.id;
        const isFreePack = ["cinematic", "minimal", "romantic", "bold_gradient", "faith"].includes(packId);
        if (!isFreePack && !isPremium) {
            if (onPremiumLocked) onPremiumLocked(packId);
            return;
        }
        // Apply the FIRST (free) variant of the chosen pack as the override.
        if (free) {
            setRecipeOverride(moodId, `${packId}.${free}`);
            tap();
            track("card_pack_iterated", { mood_id: moodId, pack: packId, from: currentPack });
            if (onChange) onChange(packId);
        }
    };

    const FREE_PACK_IDS = ["cinematic", "minimal", "romantic", "bold_gradient", "faith", "receipt", "newspaper", "ticket", "love_letter", "pen_paper"];

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.4 }}
            className="w-full"
            data-testid="pack-iteration-strip"
        >
            <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                    <Shapes className="w-3.5 h-3.5 text-amber-200/70" strokeWidth={1.5} />
                    <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-2">
                        try another look
                    </span>
                </div>
                <span className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-65">
                    tap to iterate
                </span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-hide" data-testid="pack-iteration-chips">
                {DEFAULT_PACKS.map((id) => {
                    const pack = STYLE_PACKS[id];
                    if (!pack) return null;
                    const isCurrent = currentPack === id;
                    const isFreePack = FREE_PACK_IDS.includes(id);
                    const isLocked = !isFreePack && !isPremium;
                    const preview = pack.variants[0];
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => handlePick(id)}
                            data-testid={`pack-iter-${id}`}
                            data-locked={isLocked ? "true" : "false"}
                            data-current={isCurrent ? "true" : "false"}
                            className={`btn-press snap-start flex-shrink-0 flex flex-col items-center gap-1.5 transition-transform hover:-translate-y-0.5 ${
                                isCurrent ? "scale-105" : ""
                            }`}
                        >
                            <div
                                className={`relative w-[44px] h-[68px] rounded-md overflow-hidden border-2 transition-colors ${
                                    isCurrent
                                        ? "border-amber-300/85"
                                        : "border-white/10 hover:border-white/30"
                                } ${isLocked ? "" : ""}`}
                                style={{
                                    background: preview.background,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: isCurrent
                                        ? "0 0 0 1px rgba(255,210,140,0.35), 0 8px 16px rgba(0,0,0,0.4)"
                                        : "inset 0 0 12px rgba(0,0,0,0.25)",
                                }}
                            >
                                {isCurrent && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                                        <Check className="w-4 h-4 text-amber-200 drop-shadow-lg" strokeWidth={2.6} />
                                    </div>
                                )}
                                {isLocked && (
                                    <>
                                        {/* iter-71: locked preview — gentle
                                            shimmer hint so the swatch still
                                            reads as "alive" content the user
                                            could have, not just a greyed out
                                            cell. */}
                                        <div
                                            className="absolute inset-0 pointer-events-none"
                                            style={{
                                                background: "linear-gradient(110deg, transparent 35%, rgba(255,210,140,0.18) 50%, transparent 65%)",
                                                animation: "shimmer 3.4s ease-in-out infinite",
                                            }}
                                        />
                                        <div className="absolute top-1 right-1">
                                            <Lock className="w-2.5 h-2.5 text-amber-100/90" strokeWidth={2} />
                                        </div>
                                    </>
                                )}
                            </div>
                            <span
                                className={`font-sans text-[8px] uppercase tracking-[0.22em] transition-colors ${
                                    isCurrent ? "text-amber-100" : "text-app-3"
                                }`}
                            >
                                {pack.label.toLowerCase()}
                            </span>
                        </button>
                    );
                })}
            </div>
            {!isPremium && (
                <p className="mt-1 px-1 font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 opacity-65">
                    5 free · <span className="text-amber-200/80">unlock all 11 with premium</span>
                </p>
            )}

            {/* ── Tone regenerate row (iter-70) ──
                Tapping a tone fires an LLM regenerate with the new tone hint.
                Locked behind the same per-card budget as initial generation —
                if the user is at quota, the API returns 402 and the parent
                surfaces the premium modal. Active tone gets the amber pill;
                an in-flight regen shows a loader on the tapped chip. */}
            <div className="mt-4 pt-3 border-t border-white/8">
                <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                        <Wand2 className="w-3.5 h-3.5 text-amber-200/70" strokeWidth={1.5} />
                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-2">
                            regenerate with tone
                        </span>
                    </div>
                    <span className="font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 opacity-65">
                        {isPremium ? "unlimited" : `${Math.max(0, regenLimit - regenCount)} of ${regenLimit} left`}
                    </span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                    {[
                        { id: null,         label: "auto" },
                        { id: "tender",     label: "tender" },
                        { id: "bold",       label: "bold" },
                        { id: "reflective", label: "reflective" },
                        { id: "faith",      label: "faith" },
                        { id: "playful",    label: "playful" },
                    ].map((t) => {
                        const active = (currentTone || null) === t.id;
                        const inFlight = regenTone === t.id;
                        const otherInFlight = regenTone !== null && regenTone !== t.id;
                        return (
                            <button
                                key={t.label}
                                type="button"
                                onClick={() => handleToneTap(t.id)}
                                disabled={!!regenTone}
                                data-testid={`tone-regen-${t.label}`}
                                data-active={active ? "true" : "false"}
                                data-pending={inFlight ? "true" : "false"}
                                className={`btn-press flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-sans text-[11px] tracking-wide transition-colors border ${
                                    active
                                        ? "bg-amber-200/15 border-amber-200/65 text-amber-100"
                                        : "border-white/10 bg-white/[0.03] text-app-2 hover:border-white/25 hover:bg-white/[0.06]"
                                } ${otherInFlight ? "opacity-40" : ""}`}
                            >
                                {inFlight && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />}
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                <AnimatePresence>
                    {regenTone && (
                        <motion.p
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="mt-2 px-1 font-serif italic text-[12px] text-amber-100/80"
                            data-testid="tone-regen-status"
                        >
                            rewriting in {regenTone === null ? "auto" : regenTone} voice…
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}
